import { useState, useEffect, useRef } from 'react';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { MessageSquare, Send, Search, ArrowLeft, Users, Plus, X, ChevronLeft, ChevronRight } from 'lucide-react';
import api from '../api/apiClient';
import { queryClient } from '../lib/queryClient';
import { useAuth } from '../contexts/AuthContext';
import PageHeader from '../components/PageHeader';
import { EmptyState } from '../components/design-system/EmptyState';

interface Participant {
  email: string;
  name: string;
  role: string;
}

interface Message {
  id: string;
  threadType: string;
  threadId: string;
  senderEmail: string;
  senderName: string;
  senderRole: string;
  body: string;
  createdAt: string;
  isSystemMessage: boolean;
}

interface MessageThread {
  threadType: string;
  threadId: string;
  threadTitle: string;
  messages: Message[];
  participants: Participant[];
  unreadCount: number;
  readOnly?: boolean;
}

interface Lease {
  id: number;
  propertyAddress: string;
  tenantName?: string;
  tenantEmail?: string;
  ownerName?: string;
  ownerEmail?: string;
  agentName?: string;
  agentEmail?: string;
  status?: string;
  hasActiveMaintenanceJob?: boolean;
  contractorName?: string;
}

export default function Messages() {
  const [searchParams] = useSearchParams();
  const { role } = useAuth();
  const [page, setPage] = useState(0);
  const PAGE_SIZE = 20;
  const { data: threadsPage, isLoading: threadsLoading } = useQuery<MessageThread[]>({
    queryKey: ['messages', 'threads', page],
    queryFn: () => api.get<MessageThread[]>(`/messages/threads?page=${page}&size=${PAGE_SIZE}`).then((r: MessageThread[] | null) => r ?? []),
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  });
  const threads = threadsPage ?? [];
  const hasMore = threads.length === PAGE_SIZE;
  const loading = threadsLoading;
  const [selectedThread, setSelectedThread] = useState<MessageThread | null>(null);
  const [loadingThread, setLoadingThread] = useState(false);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const currentUserEmail = localStorage.getItem('rentry_user_email') || '';

  // New conversation modal state
  const [showNewConversation, setShowNewConversation] = useState(false);
  const [leases, setLeases] = useState<Lease[]>([]);
  const [selectedLease, setSelectedLease] = useState<number | null>(null);
  const [selectedRecipientType, setSelectedRecipientType] = useState<'tenant' | 'owner' | 'contractor'>('tenant');
  const [initialMessage, setInitialMessage] = useState('');
  const [startingConversation, setStartingConversation] = useState(false);

  useEffect(() => {
    loadLeases();
  }, []);

  // Handle URL parameters to auto-open a thread
  useEffect(() => {
    const type = searchParams.get('type');
    const id = searchParams.get('id');
    if (type && id) {
      loadThread(type, id);
    }
  }, [searchParams]);

  useEffect(() => {
    scrollToBottom();
  }, [selectedThread?.messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const loadThreads = async () => {
    await queryClient.invalidateQueries({ queryKey: ['messages', 'threads'] });
  };

  const loadLeases = async () => {
    try {
      let response: any;

      // Load leases based on user role
      if (role === 'AGENT') {
        response = await api.get<any[]>('/agent/leases');
        if (Array.isArray(response)) {
          setLeases(response.map((l: any) => ({
            id: l.id,
            propertyAddress: l.propertyAddress || 'Property',
            tenantName: l.tenantName,
            tenantEmail: l.tenantEmail,
            ownerName: l.ownerName,
            ownerEmail: l.ownerEmail,
            status: l.status,
            hasActiveMaintenanceJob: false, // TODO: Get from API
          })));
        }
      } else if (role === 'OWNER') {
        response = await api.get<any[]>('/owner/leases');
        const leaseList = Array.isArray(response) ? response : (response?.leases || []);
        // Show all leases that have an agent (owners can always message their agent)
        setLeases(leaseList
          .filter((l: any) => l.agentName)
          .map((l: any) => ({
            id: l.id,
            propertyAddress: l.propertyAddress || 'Property',
            agentName: l.agentName,
            agentEmail: l.agentEmail,
            status: l.status,
          })));
      } else {
        // Renter - only active leases
        response = await api.get<{ leases: any[] }>('/renter/onboarding/available-leases');
        if (response?.leases) {
          setLeases(response.leases
            .filter((l: any) => l.status === 'ACTIVE')
            .map((l: any) => ({
              id: l.leaseId || l.id,
              propertyAddress: l.propertyAddress || l.address || 'Property',
              status: l.status,
            })));
        }
      }
    } catch (err) {
      console.error('Failed to load leases:', err);
    }
  };

  const loadThread = async (type: string, id: string) => {
    setLoadingThread(true);
    try {
      const response = await api.get<MessageThread>(`/messages/thread/${type}/${id}`);
      setSelectedThread(response);
    } catch (err: any) {
      setError('Failed to load conversation');
    } finally {
      setLoadingThread(false);
    }
  };

  const handleSelectThread = (thread: MessageThread) => {
    loadThread(thread.threadType, thread.threadId);
  };

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newMessage.trim() || !selectedThread) return;

    setSending(true);
    try {
      const response = await api.post<Message>('/messages', {
        threadType: selectedThread.threadType,
        threadId: selectedThread.threadId,
        body: newMessage.trim()
      });

      // Add the new message to the thread
      setSelectedThread(prev => prev ? {
        ...prev,
        messages: [...(prev.messages || []), response]
      } : null);

      setNewMessage('');
    } catch (err: any) {
      setError('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const handleStartConversation = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedLease || !initialMessage.trim()) return;

    setStartingConversation(true);
    try {
      // Determine thread type based on user role
      let threadType = 'LEASE';
      if (role === 'OWNER') {
        threadType = 'LEASE_OWNER';
      } else if (role === 'RENTER') {
        threadType = 'LEASE_RENTER';
      } else if (role === 'AGENT') {
        // For agents, use the selected recipient type
        threadType = selectedRecipientType === 'owner' ? 'LEASE_OWNER' : 'LEASE_RENTER';
      }

      // Send the initial message to create/access the thread
      await api.post<Message>('/messages', {
        threadType: threadType,
        threadId: selectedLease.toString(),
        body: initialMessage.trim()
      });

      // Close modal and reload threads
      setShowNewConversation(false);
      setSelectedLease(null);
      setSelectedRecipientType('tenant');
      setInitialMessage('');

      await loadThreads();

      // Load and select the new thread
      loadThread(threadType, selectedLease.toString());
    } catch (err: any) {
      setError('Failed to start conversation. Please try again.');
    } finally {
      setStartingConversation(false);
    }
  };

  const formatTime = (dateString: string) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;

    return date.toLocaleDateString('en-AU', {
      month: 'short',
      day: 'numeric'
    });
  };

  const formatMessageTime = (dateString: string) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-AU', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getLastMessage = (thread: MessageThread) => {
    if (!thread.messages || thread.messages.length === 0) return 'No messages yet';
    const lastMsg = thread.messages[thread.messages.length - 1];
    return lastMsg.body.length > 50 ? lastMsg.body.substring(0, 50) + '...' : lastMsg.body;
  };

  const getLastMessageTime = (thread: MessageThread) => {
    if (!thread.messages || thread.messages.length === 0) return '';
    return formatTime(thread.messages[thread.messages.length - 1].createdAt);
  };

  const getRoleColor = (role: string) => {
    switch (role?.toUpperCase()) {
      case 'AGENT': return 'bg-blue-100 text-blue-800';
      case 'OWNER': return 'bg-purple-100 text-purple-800';
      case 'RENTER': return 'bg-green-100 text-green-800';
      case 'CONTRACTOR': return 'bg-orange-100 text-orange-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const filteredThreads = threads.filter(thread =>
    thread.threadTitle?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    thread.participants?.some(p => p.name?.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <PageHeader
        title="Messages"
        subtitle="Communicate with your property manager and support team"
        icon={<MessageSquare className="w-8 h-8" />}
      >
        <button
          onClick={() => setShowNewConversation(true)}
          className="flex items-center gap-2 px-4 py-2 bg-secondary text-white rounded-lg hover:bg-secondary/90 transition-colors"
        >
          <Plus className="w-4 h-4" />
          New Conversation
        </button>
      </PageHeader>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-800 text-sm">
          {error}
          <button onClick={() => setError('')} className="ml-2 text-red-600 hover:text-red-800">×</button>
        </div>
      )}

      <div className="bg-white rounded-lg shadow overflow-hidden" style={{ height: 'calc(100vh - 250px)', minHeight: '500px' }}>
        <div className="flex h-full">
          {/* Thread List */}
          <div className={`w-full md:w-1/3 border-r border-gray-200 flex flex-col ${selectedThread ? 'hidden md:flex' : 'flex'}`}>
            {/* Search */}
            <div className="p-4 border-b border-gray-200">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search conversations..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-secondary focus:border-transparent"
                />
              </div>
            </div>

            {/* Thread List */}
            <div className="flex-1 overflow-y-auto">
              {filteredThreads.length === 0 ? (
                <EmptyState
                  icon={<MessageSquare size={36} />}
                  title={threads.length === 0 ? 'No conversations yet' : 'No matching conversations'}
                  description={
                    threads.length === 0
                      ? 'Start a conversation with your property manager, tenant, or agent.'
                      : 'Try adjusting your search query.'
                  }
                  action={
                    threads.length === 0 ? (
                      <button
                        onClick={() => setShowNewConversation(true)}
                        className="px-4 py-2 bg-forest-900 text-white rounded-10 text-sm font-medium hover:bg-forest-800 transition-colors"
                      >
                        Start a Conversation
                      </button>
                    ) : undefined
                  }
                />
              ) : (
                filteredThreads.map(thread => (
                  <div
                    key={`${thread.threadType}-${thread.threadId}`}
                    onClick={() => handleSelectThread(thread)}
                    className={`p-4 border-b border-gray-100 cursor-pointer hover:bg-gray-50 transition-colors ${
                      selectedThread?.threadId === thread.threadId ? 'bg-secondary/5 border-l-4 border-l-secondary' : ''
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                        <Users className="w-5 h-5 text-primary" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between mb-1">
                          <h3 className="font-medium text-gray-900 truncate">{thread.threadTitle}</h3>
                          <span className="text-xs text-gray-500">{getLastMessageTime(thread)}</span>
                        </div>
                        <p className="text-sm text-gray-500 truncate">{getLastMessage(thread)}</p>
                        <div className="flex items-center gap-2 mt-1">
                          {thread.readOnly && (
                            <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-500">
                              Read-only
                            </span>
                          )}
                          {thread.participants?.slice(0, 2).map(p => (
                            <span key={p.email} className={`text-xs px-2 py-0.5 rounded-full ${getRoleColor(p.role)}`}>
                              {p.name?.split(' ')[0] || p.role}
                            </span>
                          ))}
                          {thread.unreadCount > 0 && (
                            <span className="ml-auto bg-secondary text-white text-xs px-2 py-0.5 rounded-full">
                              {thread.unreadCount}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>

            {/* Pagination */}
            {(page > 0 || hasMore) && (
              <div className="flex items-center justify-between px-4 py-2 border-t border-gray-200">
                <button
                  onClick={() => setPage(p => Math.max(0, p - 1))}
                  disabled={page === 0}
                  className="flex items-center gap-1 text-sm text-gray-600 disabled:text-gray-300 hover:text-gray-900"
                >
                  <ChevronLeft size={16} /> Prev
                </button>
                <span className="text-xs text-gray-500">Page {page + 1}</span>
                <button
                  onClick={() => setPage(p => p + 1)}
                  disabled={!hasMore}
                  className="flex items-center gap-1 text-sm text-gray-600 disabled:text-gray-300 hover:text-gray-900"
                >
                  Next <ChevronRight size={16} />
                </button>
              </div>
            )}
          </div>

          {/* Message View */}
          <div className={`flex-1 flex flex-col ${selectedThread ? 'flex' : 'hidden md:flex'}`}>
            {selectedThread ? (
              <>
                {/* Thread Header */}
                <div className="p-4 border-b border-gray-200 flex items-center gap-3">
                  <button
                    onClick={() => setSelectedThread(null)}
                    className="md:hidden p-2 hover:bg-gray-100 rounded-lg"
                  >
                    <ArrowLeft className="w-5 h-5" />
                  </button>
                  <div className="flex-1">
                    <h2 className="font-semibold text-gray-900">{selectedThread.threadTitle}</h2>
                    <p className="text-sm text-gray-500">
                      {selectedThread.participants?.map(p => p.name).join(', ')}
                    </p>
                  </div>
                </div>

                {/* Messages */}
                <div className="flex-1 overflow-y-auto p-4 space-y-4">
                  {loadingThread ? (
                    <div className="flex justify-center py-8">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                    </div>
                  ) : selectedThread.messages?.length === 0 ? (
                    <div className="text-center py-8">
                      <MessageSquare className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                      <p className="text-gray-500">No messages yet. Start the conversation!</p>
                    </div>
                  ) : (
                    selectedThread.messages?.map(message => {
                      const isOwnMessage = message.senderEmail === currentUserEmail;
                      const isSystem = message.isSystemMessage;

                      if (isSystem) {
                        return (
                          <div key={message.id} className="flex justify-center">
                            <div className="bg-gray-100 text-gray-600 text-sm px-4 py-2 rounded-full">
                              {message.body}
                            </div>
                          </div>
                        );
                      }

                      return (
                        <div
                          key={message.id}
                          className={`flex ${isOwnMessage ? 'justify-end' : 'justify-start'}`}
                        >
                          <div className={`max-w-[70%] ${isOwnMessage ? 'order-2' : ''}`}>
                            {!isOwnMessage && (
                              <div className="flex items-center gap-2 mb-1">
                                <span className="text-sm font-medium text-gray-900">{message.senderName}</span>
                                <span className={`text-xs px-2 py-0.5 rounded-full ${getRoleColor(message.senderRole)}`}>
                                  {message.senderRole}
                                </span>
                              </div>
                            )}
                            <div
                              className={`rounded-lg px-4 py-2 ${
                                isOwnMessage
                                  ? 'bg-primary text-white'
                                  : 'bg-gray-100 text-gray-900'
                              }`}
                            >
                              <p>{message.body}</p>
                            </div>
                            <p className={`text-xs text-gray-500 mt-1 ${isOwnMessage ? 'text-right' : ''}`}>
                              {formatMessageTime(message.createdAt)}
                            </p>
                          </div>
                        </div>
                      );
                    })
                  )}
                  <div ref={messagesEndRef} />
                </div>

                {/* Message Input */}
                {selectedThread.readOnly ? (
                  <div className="p-4 border-t border-gray-200 bg-gray-50">
                    <p className="text-center text-gray-500 text-sm">
                      This conversation is read-only. You cannot send new messages.
                    </p>
                  </div>
                ) : (
                  <form onSubmit={handleSendMessage} className="p-4 border-t border-gray-200">
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={newMessage}
                        onChange={(e) => setNewMessage(e.target.value)}
                        placeholder="Type a message..."
                        className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-secondary focus:border-transparent"
                        disabled={sending}
                      />
                      <button
                        type="submit"
                        disabled={!newMessage.trim() || sending}
                        className="px-4 py-2 bg-secondary text-white rounded-lg hover:bg-secondary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        <Send className="w-5 h-5" />
                      </button>
                    </div>
                  </form>
                )}
              </>
            ) : (
              <div className="flex-1 flex items-center justify-center">
                <div className="text-center">
                  <MessageSquare className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">Select a conversation</h3>
                  <p className="text-gray-500 mb-4">Choose a conversation from the list to view messages</p>
                  <button
                    onClick={() => setShowNewConversation(true)}
                    className="text-secondary hover:text-primary font-medium"
                  >
                    Or start a new conversation
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* New Conversation Modal */}
      {showNewConversation && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-bold text-gray-900">New Conversation</h2>
                <button
                  onClick={() => {
                    setShowNewConversation(false);
                    setSelectedLease(null);
                    setInitialMessage('');
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>

            <form onSubmit={handleStartConversation} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select Property/Lease
                </label>
                {leases.length === 0 ? (
                  <p className="text-gray-500 text-sm">
                    {role === 'OWNER'
                      ? 'No properties with agents found. Your property needs an agent to message.'
                      : role === 'AGENT'
                      ? 'No leases found. Create a lease to message tenants or owners.'
                      : 'No properties found. You need an active lease to start a conversation.'}
                  </p>
                ) : (
                  <select
                    value={selectedLease || ''}
                    onChange={(e) => setSelectedLease(Number(e.target.value) || null)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-secondary focus:border-transparent"
                    required
                  >
                    <option value="">Select a property...</option>
                    {leases.map(lease => (
                      <option key={lease.id} value={lease.id}>
                        {lease.propertyAddress}
                        {lease.tenantName && ` - Tenant: ${lease.tenantName}`}
                        {lease.agentName && ` - Agent: ${lease.agentName}`}
                      </option>
                    ))}
                  </select>
                )}
              </div>

              {/* Recipient selection for agents */}
              {role === 'AGENT' && selectedLease && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Message To
                  </label>
                  <div className="space-y-2">
                    {(() => {
                      const lease = leases.find(l => l.id === selectedLease);
                      const isActive = lease?.status === 'ACTIVE';
                      return (
                        <>
                          <label className={`flex items-center p-3 border rounded-lg cursor-pointer ${
                            selectedRecipientType === 'tenant' ? 'border-primary bg-primary/5' : 'border-gray-200'
                          } ${!isActive ? 'opacity-50 cursor-not-allowed' : ''}`}>
                            <input
                              type="radio"
                              name="recipient"
                              value="tenant"
                              checked={selectedRecipientType === 'tenant'}
                              onChange={() => setSelectedRecipientType('tenant')}
                              disabled={!isActive}
                              className="mr-3"
                            />
                            <div>
                              <p className="font-medium">Tenant</p>
                              <p className="text-sm text-gray-500">
                                {lease?.tenantName || 'No tenant'}
                                {!isActive && ' (lease not active)'}
                              </p>
                            </div>
                          </label>
                          <label className={`flex items-center p-3 border rounded-lg cursor-pointer ${
                            selectedRecipientType === 'owner' ? 'border-primary bg-primary/5' : 'border-gray-200'
                          }`}>
                            <input
                              type="radio"
                              name="recipient"
                              value="owner"
                              checked={selectedRecipientType === 'owner'}
                              onChange={() => setSelectedRecipientType('owner')}
                              className="mr-3"
                            />
                            <div>
                              <p className="font-medium">Owner</p>
                              <p className="text-sm text-gray-500">{lease?.ownerName || 'Property owner'}</p>
                            </div>
                          </label>
                          {lease?.hasActiveMaintenanceJob && (
                            <label className={`flex items-center p-3 border rounded-lg cursor-pointer ${
                              selectedRecipientType === 'contractor' ? 'border-primary bg-primary/5' : 'border-gray-200'
                            }`}>
                              <input
                                type="radio"
                                name="recipient"
                                value="contractor"
                                checked={selectedRecipientType === 'contractor'}
                                onChange={() => setSelectedRecipientType('contractor')}
                                className="mr-3"
                              />
                              <div>
                                <p className="font-medium">Contractor</p>
                                <p className="text-sm text-gray-500">{lease?.contractorName || 'Assigned contractor'}</p>
                              </div>
                            </label>
                          )}
                        </>
                      );
                    })()}
                  </div>
                </div>
              )}

              {role === 'OWNER' && (
                <p className="text-sm text-gray-500">As an owner, you can only message your property's agent.</p>
              )}
              {role === 'RENTER' && (
                <p className="text-sm text-gray-500">You can message your property manager about lease-related matters.</p>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Your Message
                </label>
                <textarea
                  value={initialMessage}
                  onChange={(e) => setInitialMessage(e.target.value)}
                  placeholder={
                    role === 'OWNER'
                      ? 'Type your message to the agent...'
                      : role === 'AGENT'
                      ? 'Type your message...'
                      : 'Type your message to the property manager...'
                  }
                  rows={4}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-secondary focus:border-transparent resize-none"
                  required
                />
              </div>

              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => {
                    setShowNewConversation(false);
                    setSelectedLease(null);
                    setSelectedRecipientType('tenant');
                    setInitialMessage('');
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={!selectedLease || !initialMessage.trim() || startingConversation}
                  className="flex-1 px-4 py-2 bg-secondary text-white rounded-lg hover:bg-secondary/90 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {startingConversation ? 'Starting...' : 'Start Conversation'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}


